import type { FC, ComponentType } from 'react';
import { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import {
  BiData,
  BiEvent,
  BiContextProvidedValue,
  BuyCoreButtonBiEvent,
  DefaultBiEvent,
  DreamupInfoClickBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { type Props as IconProps } from '@wix/da-ds/pkg/Icons/IconWrap';
import DreamUpStars from '@wix/da-ds/pkg/Icons/16x16/DreamUpStars';
import BoostFilled from '@wix/da-ds/pkg/Icons/16x16/BoostFilled';
import GiveBadge from '@wix/da-ds/pkg/Icons/16x16/GiveBadge';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import BuyCoreTextButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreTextButton';

import s from './BenefitBlock.scss';

type BenefitValue = {
  // Prompts
  freePromptsAllowed?: number;
  freePromptsRemaining?: number;

  // Boosts
  freeBoostsAllowed?: number;
  freeBoostsRemaining?: number;

  // Gems
  allocation?: number;
  remaining?: number;
};

export interface Props {
  type: string;
  value: BenefitValue;
}

const BenefitBlock: FC<Props> = ({ type, value }) => {
  const { t: baseT } = useTranslation();
  const t = (key: string, values: any = {}) =>
    baseT(`studio.home.benefit.${type}.${key}`, values);
  const { hasCoreMembership, coreLevel } = useContext(SessionContext);

  let Icon: ComponentType<IconProps>,
    allocation: number,
    remaining: number,
    isCoreState: boolean,
    ctaVariant: 'edit-purple' | 'approval-green' | 'main',
    ctaUrl: string,
    biData: BiEvent,
    upgradeReferral: ReferralType;

  switch (type) {
    case 'prompts':
      Icon = DreamUpStars;
      allocation = value.freePromptsAllowed ?? 0;
      remaining = value.freePromptsRemaining ?? 0;
      isCoreState = hasCoreMembership;
      ctaVariant = 'edit-purple';
      ctaUrl = Url.dreamupLink();
      biData = BiData<DreamupInfoClickBiEvent>({
        evid: 923,
        sectionname: BiContextProvidedValue,
      });
      upgradeReferral = ReferralType.STUDIO_HOME_DREAMUP;
      break;

    case 'boosts':
      Icon = BoostFilled;
      allocation = value.freeBoostsAllowed ?? 0;
      remaining = value.freeBoostsRemaining ?? 0;
      isCoreState = hasCoreMembership && coreLevel !== 'core_access';
      ctaVariant = 'approval-green';
      ctaUrl = Url.studio({ section: 'published' });
      biData = BiData<DefaultBiEvent>({
        evid: 2,
        click_info: 'studio_home_boost',
        sectionname: BiContextProvidedValue,
      });
      upgradeReferral = ReferralType.STUDIO_HOME_BOOST;
      break;

    case 'gems':
      Icon = GiveBadge;
      allocation = value.allocation ?? 0;
      remaining = value.remaining ?? 0;
      isCoreState = hasCoreMembership;
      ctaUrl = Url.browseHomeLink();
      ctaVariant = 'main';
      biData = BiData<DefaultBiEvent>({
        evid: 2,
        click_info: 'studio_home_award_badge',
        sectionname: BiContextProvidedValue,
      });
      upgradeReferral = ReferralType.STUDIO_HOME_AWARD_BADGE;
      break;

    default:
      return null;
  }
  
  return (
    <div className={c(s['root'], s[`type-${type}`])}>
      <div className={s['title']}>
        <span className={s['icon']}>
          <Icon disableDefaultColors size={IconSize.SMALLER} />
        </span>
        <span>{t(`title.${isCoreState ? 'core' : 'nonCore'}`)}</span>
      </div>
      <div className={s['progress']}>
        {t('progress', { allocation, remaining })}
        <span className={s['bar']} />
        {remaining > 0 && (
          <span
            className={s['bar-remaining']}
            style={{
              width: `${remaining / allocation * 100.0}%`,
            }}
          />
        )}
      </div>
      <div className={s['blurb']}>
        {t(`blurb.${isCoreState ? 'core' : 'nonCore'}`)}
      </div>
      {remaining > 0 && (
        <TextButton
          className={s['cta']}
          variant={ctaVariant}
          size="medium"
          href={ctaUrl}
          biData={biData}
        >
          {t('cta')}
        </TextButton>
      )}
      {remaining === 0 && coreLevel !== 'core_max' && (
        <BuyCoreTextButton
          className={c(s['cta'], s['cta-core'])}
          size="medium"
          noHorizontalPaddings
          biData={BiData<BuyCoreButtonBiEvent>({
            evid: 375,
            referral_info: upgradeReferral,
          })}
        >
          <CoreSymbol corePackage="core_plus" size={IconSize.SMALL} />{' '}
          <span className={s['cta-core-text']}>
            {baseT('common.upgradeCoreCTA')}
          </span>
        </BuyCoreTextButton>
      )}
    </div>
  );
}
BenefitBlock.displayName = 'BenefitBlock';
export default BenefitBlock;
